import * as THREE from 'three';
import { solveLimb, track } from './character-motion.js';
import type { TunedBody } from './character-animator.js';
import { HumanAnimator, type HumanFit } from './human-animator.js';

/** The male's `TunedBody.stride`: the gait clips she shares are keyed on him. */
const MALE_STRIDE = .85953;
/** What the clips were tuned on: see `TunedBody`. */
const TUNED: TunedBody = { leg: .85746, arm: .58446, torso: .51876, shoulder: [.14941, .188, -.01974], pivot: .09292, head: { y: 1.56862, z: .01598 }, stride: .75921 };
const FIT: HumanFit = { body: 'female', bowRecoil: .9, sharedStride: MALE_STRIDE, kneeTrack: (amount, run) => 1 - THREE.MathUtils.lerp(.6 * amount, .65, run) };

/** Poses the female human rig: light and upright. Its motion is its clips; this file keeps what is its own beyond them. */
export class FemaleAnimator extends HumanAnimator {
  /** The human female these clips were tuned on (as of the 14 Oct 2026 re-sculpt); other bodies and later re-sculpts are posed relative to it. */
  protected override tunedBody(): TunedBody { return TUNED; }
  protected humanFit(): HumanFit { return FIT; }
  poseMounted(seat: THREE.Vector3, foot: THREE.Vector3, rein: THREE.Vector3, bob: number, ride = 0) {
    const rig = this.rig;
    const p = this.time === undefined ? undefined : this.progress(this.action, this.time);
    const striking = p !== undefined && (this.action?.kind === 'thrust' || this.action?.kind === 'slash');
    const brace = striking ? track(p, [[0, 0], [.32, -.045], [.52, .065], [.63, .08], [1, 0]]) : 0;
    const turn = striking ? THREE.MathUtils.clamp(rig.hips.rotation.y * .35, -.14, .14) : 0;
    rig.hips.position.set(seat.x, seat.y + .065 + bob, seat.z);
    // At a gallop the rider crouches low over the pommel; a strike rises out of the crouch and both hands let go only for it.
    const strike = striking ? track(p, [[0, 0], [.14, 1], [.86, 1], [1, 0]]) : 0, crouch = ride * (1 - strike * .6);
    rig.hips.rotation.set(.06 + brace, turn, 0);
    this.crouchInSaddle(crouch);
    for (const [leg, side] of [[rig.leftLeg, -1], [rig.rightLeg, 1]] as const) {
      const inverse = rig.hips.quaternion.clone().invert();
      const target = new THREE.Vector3(side * foot.x, foot.y + bob, foot.z).sub(rig.hips.position).applyQuaternion(inverse);
      solveLimb(leg, target, new THREE.Vector3(side * .85, 0, 1).applyQuaternion(inverse), 1, 'knee');
      const chain = rig.hips.quaternion.clone().multiply(leg.upper.quaternion).multiply(leg.lower.quaternion);
      leg.hand.quaternion.copy(chain.invert()).multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(.08, side * .18, 0)));
    }
    this.refreshBones();
    const arm = rig.leftArm, parent = arm.upper.parent!;
    const grip = parent.worldToLocal(rig.root.localToWorld(rein.clone().add(new THREE.Vector3(0, bob, 0))));
    solveLimb(arm, grip, new THREE.Vector3(-.45, -.25, .35), 1);
    arm.hand.rotation.set(.12, 0, -.12);
    if (!ride) return;
    const weapon = rig.rightArm, shoulder = weapon.upper.parent!;
    solveLimb(weapon, shoulder.worldToLocal(rig.root.localToWorld(rein.clone().add(new THREE.Vector3(.09, bob - .015, -.02)))), new THREE.Vector3(.45, -.25, .35), ride * (1 - strike));
  }
  /** A dance's share of the pose, and whether it keeps the feet close (the knees then point ahead). */
}
